import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import logo from '../assets/logo.png';
import logo2 from '../assets/logo2.png';
import './CSS/Login.css';

const COST_PER_VERIFY = 1; // deduct 1 credit per verification

const VerifySlipx = ({ history, location }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [timestamp, setTimestamp] = useState(null);

  // User meta
  const [email, setEmail] = useState('');
  const [balance, setBalance] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // === Load user details ===
  useEffect(() => {
    const state = location && location.state;
    if (state && state.email) {
      // ✅ Use data from login redirect (instant display)
      setEmail(state.email);
      setBalance(Number(state.balance || 0));
      console.log('Loaded from router state:', state);
    } else {
      // 🔁 Fallback to backend (if user refreshed)
      console.log('No state found, fetching from backend...');
      fetchUserDetails();
    }
  }, [location]);

  const fetchUserDetails = async () => {
    try {
      const res = await fetch('/user-details');
      const data = await res.json();
      setEmail(data.email || '');
      setBalance(Number(data.balance || 0));
      console.log('Fetched from /user-details:', data);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setMessage('⚠️ Failed to fetch user data.');
    }
  };

  // === File selection ===
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && (selectedFile.type === 'image/jpeg' || selectedFile.type === 'image/png')) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setMessage('');
      setIsSuccess(false);
      setApiData(null);
      setTimestamp(null);
    } else {
      setMessage('Please upload a valid JPEG or PNG file.');
      setFile(null);
      setPreview(null);
      setApiData(null);
      setTimestamp(null);
    }
  };

  // === Submit to backend ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage('Please upload a payment slip before verifying.');
      return;
    }

    if (balance < COST_PER_VERIFY) {
      setMessage('❌ Not enough credits to verify the slip.');
      return;
    }

    const confirm = window.confirm(
      `Verify slip now?\nThis will deduct ${COST_PER_VERIFY} credit.\n` +
      `Current balance: ${balance.toFixed(2)} → After: ${(balance - COST_PER_VERIFY).toFixed(2)}`
    );
    if (!confirm) return;

    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('paymentSlip', file);

      // Deduct balance first
      const newBalance = balance - COST_PER_VERIFY;
      const balanceRes = await fetch('/update-balance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, newBalance }),
      });

      if (!balanceRes.ok) {
        const err = await balanceRes.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to update balance.');
      }
      setBalance(newBalance);

      // Then verify slip
      const response = await fetch('/verify-slipx', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      console.log('Backend response:', result);

      setApiData(result.details || null);
      setTimestamp(result.timestamp || null);

      if (response.ok && result.isReal) {
        setMessage('✅ Verified: This slip is REAL and issued by the bank.');
        setIsSuccess(true);
      } else {
        setMessage(`❌ This slip is NOT verified: ${result.message || 'Unknown error.'}`);
        setIsSuccess(false);
      }
    } catch (error) {
      console.error(error);
      setMessage('⚠️ An error occurred while verifying the slip.');
      setIsSuccess(false);
    } finally {
      setIsProcessing(false);
    }
  };

  // === Format date in Thai ===
  const formatThaiDate = (isoDate) => {
    try {
      const date = new Date(isoDate);
      return date.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  // === Show readable details ===
  const renderReadableApiData = (data) => {
    if (!data) return null;

    const readable = {
      'Verification Result': isSuccess ? '✅ Real Slip' : '❌ Fake Slip',
      'Payment Timestamp (ISO)': timestamp || 'N/A',
      'Payment Time (Local Thai)': timestamp ? formatThaiDate(timestamp) : 'N/A',
      'Bank Name': data.receiver?.bank?.name || 'N/A',
      'Sender Name': data.sender?.account?.name?.th || 'N/A',
      'Receiver Name': data.receiver?.account?.name?.th || 'N/A',
      'Receiver Account': data.receiver?.account?.proxy?.account || 'N/A',
      'Amount': data.amount?.amount ? `฿${data.amount.amount.toFixed(2)}` : 'N/A',
      'Transaction ID': data.transRef || 'N/A',
    };

    return (
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '10px',
        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
        padding: '20px',
        marginTop: '20px',
        textAlign: 'left',
        width: '100%',
      }}>
        <h3>🧾 Verification Details</h3>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {Object.entries(readable).map(([key, value]) => (
            <li key={key} style={{ marginBottom: '8px' }}>
              <strong>{key}:</strong> <span>{value}</span>
            </li>
          ))}
        </ul>

        <h4 style={{ marginTop: '15px' }}>📦 Full API JSON:</h4>
        <pre style={{
          backgroundColor: '#f8f8f8',
          padding: '10px',
          borderRadius: '8px',
          fontSize: '13px',
          overflowX: 'auto'
        }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      minHeight: '100vh',
      background: '#f2f4f8',
      justifyContent: 'space-between'
    }}>
      <div style={{
        maxWidth: '500px',
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <img src={logo} alt="Logo" style={{ width: '200px', marginBottom: '20px' }} />
        <h2>Bank Slip Verification Service</h2>
        <p>Upload your slip and verify if it’s real or fake (deducts 1 credit).</p>

        {/* User info */}
        <div className="mb-2">
          <strong>Email:</strong> {email || '—'} &nbsp;|&nbsp;
          <strong>Balance:</strong> {balance.toFixed(2)} credits
        </div>

        <form onSubmit={handleSubmit}>
          <label htmlFor="fileInput" style={{ fontWeight: 'bold' }}>Select Payment Slip (JPEG/PNG):</label>
          <input
            type="file"
            id="fileInput"
            accept="image/jpeg, image/png"
            onChange={handleFileChange}
            style={{ display: 'block', margin: '10px auto' }}
          />

          {preview && (
            <div style={{ marginTop: '20px' }}>
              <h3>Preview:</h3>
              <img
                src={preview}
                alt="Slip Preview"
                style={{
                  maxWidth: '100%',
                  height: '250px',
                  border: '2px solid #ddd',
                  borderRadius: '10px',
                  padding: '5px'
                }}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isProcessing || balance < COST_PER_VERIFY}
            style={{
              padding: '10px 20px',
              cursor: 'pointer',
              marginTop: '20px',
              backgroundColor: isProcessing ? '#888' : isSuccess ? '#28a745' : '#ff0000',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            {isProcessing ? 'Verifying…' : `Verify Slip (−${COST_PER_VERIFY} credit)`}
          </button>
        </form>

        {message && (
          <p style={{
            marginTop: '15px',
            color: isSuccess ? 'green' : 'red',
            fontWeight: 'bold'
          }}>
            {message}
          </p>
        )}

        {renderReadableApiData(apiData)}
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: 'auto', paddingBottom: '20px' }}>
        <p style={{ fontSize: '14px', marginBottom: '10px' }}>Powered by</p>
        <img src={logo2} alt="Logo2" style={{ width: '100px', opacity: 0.8 }} />
        <div>New Wave Consulting Group</div>
      </div>
    </div>
  );
};

export default withRouter(VerifySlipx);
